import { formatDate } from '@angular/common';
import { EdependencyLevel, TLoadDependeciesParams } from '@df/catalog/model';
import {
    Product as CoreProduct,
    IProductData,
    IProductDataV2,
    IProductDataV3,
    IProductMediaItem,
    Product
} from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { CacheService } from '@df/core/cache/cache';
import { NgInject } from '@df/ng/ng-inject';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { NUi } from '@df/ui/df-ui.config';
import { TaxMode } from '@df/ui/price/price.service';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { Nexus } from '@tomandco/nexus-api-types';
import { NAttributeOrder } from '../../../cms/config/attribute-order.cms-config';
import { NProductSizeGuideModal } from '../../../cms/modal/product-size-guide/product-size-guide-modal.interface';
import { NPaypalPayInThreeMessageComponent } from '../../../module/paypal-pay-in-three/paypal-pay-in-three-message.component';
import { VipService } from '../../../module/vip/vip.service';
import { TradeStoreService } from '../../../multisite/trade/trade-store.service';
import { NCmsProductKlarnaMsg } from '../../ui/product/product-view-klarna-msg/product-view-klarna-msg.component';
import { NProductViewSurchargeNote } from '../../ui/product/surcharge/note/product-view-surcharge-note.component';
import { NProductViewLayout } from '../../ui/product/view-layout/product-view-layout.interface';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Catalog.Type {
        interface IProductMediaItem {
            landscape: boolean;
            alt_text?: string;
            image_title?: string;
        }
    }
}

declare module '@df/catalog/product/product' {
    interface IProductDataV1 {
        archive?: 0 | 1;
    }
    interface IProductDataV2 {
        media?: Nexus.Lib.Catalog.Type.IProductMediaItem[];
        color?: number | number[];
        size?: number;
        banner_type?: number;
        in_stock_date?: string;
        tier_price?: {
            [key: string]: {
                price: number;
            };
        };
        ecologi?: number;
        phase?: EProductPhase;
        force_coming_soon?: 0 | 1;
        color_org?: number;
        min_sales_qty_website?: number;
        qty_increment_website?: number;
        plp_label?: string;
        // Outfit builder
        eligible_builder?: boolean;
        toy_eligible_builder?: boolean;
        gender?: number | number[];
        horse_type?: number | number[];
        surcharge?: number;
        banner_url?: string;
        is_ob_bundle?: boolean;
        custom_url?: string;
        inherit_reviews_from_following?: string;
        isBanner?: boolean;
        isPOS?: boolean;
    }
    interface IProductDataV3 {
        care?: string;
        feature?: number[];
        fashion_recommendation?: number[];
        giftcard_occasion?: number;
        product_page?: string;
        size_guide?: number;
        default_product_id_shown?: number;
        gs1_barcode: number;
        rrp: number;
        surcharge?: number;
        features_section?: number[];
        icms_block_order?: string;
        specification_section?: number[];
        has_free_gift?: boolean;
        faq?: string;
    }
}
export enum EListingLabelOptionId {
    newIn = 5900,
    sale = 5901,
    comingSoon = 5902,
    preOrder = 5903,
    backSoon = 7605,
    soldOut = 7606,
    backOrder = 7607
}
export enum EProductPhase {
    current = 7001,
    discontinued = 7002,
    new = 7000
}

export enum EProductStatus {
    comingSoonForced,
    comingSoon,
    preorder,
    backSoon,
    backorder,
    soldOut,
    unknown
}

export type TProductCmsSection =
    | 'video'
    | 'tech'
    | 'aswornby'
    | 'carousel'
    | 'highlights'
    | 'specs'
    | 'editorial'
    | 'reviews'
    | 'technical'
    | 'features'
    | 'athlete';
export const HIDDEN_LABEL_MEDIA = ['outfit_builder_additional', 'outfit_builder_image'];

export class LocalProduct<T extends IProductData = IProductData> extends CoreProduct<T> {
    /**
     * Size guide block object if loaded
     */
    sizeGuideBlock?: ICmsBlock<NProductSizeGuideModal.IBlockData>;

    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    @NgInject(ICmsConfigResource)
    private iCmsConfigResource?: ICmsConfigResource;

    @NgInject(TradeStoreService)
    private tradeStoreService!: TradeStoreService;

    @NgInject(CacheService)
    private cacheService!: CacheService;

    @NgInject(NUi.OPTIONS)
    private uiConfig!: NUi.IModuleOptions;

    @NgInject(VipService)
    private vipService!: VipService;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const loader = params.loader;

        if (this.verbosity! > 2 && params.dependencyLevel === EdependencyLevel.details) {
            if (this.ppLayoutVariant) {
                loader.add(
                    this.iCmsBlockResource.retrieve(
                        {
                            bareUrl: NProductViewLayout.BLOCK_ID,
                            variant: this.ppLayoutVariant,
                            createOnError: true
                        },
                        params
                    )
                );
            }
            if ((this.data as IProductDataV3).surcharge) {
                loader.add(
                    this.iCmsBlockResource.retrieve(
                        {
                            bareUrl: NProductViewSurchargeNote.BLOCK_ID,
                            variant: 'product',
                            createOnError: true
                        },
                        params
                    )
                );
            }

            if (this.sizeGuideVariant) {
                const promise = this.iCmsBlockResource.retrieve(
                    {
                        bareUrl: NProductSizeGuideModal.BLOCK_ID,
                        variant: this.sizeGuideVariant,
                        createOnError: true
                    },
                    params
                );
                promise.then(block => {
                    this.sizeGuideBlock = (block as ICmsBlock<NProductSizeGuideModal.IBlockData>) || undefined;
                });
                loader.add(promise);
            }

            if (this.iCmsConfigResource) {
                loader.add(
                    this.iCmsConfigResource.retrieve(
                        {
                            bareUrl: NAttributeOrder.ID
                        },
                        params
                    )
                );
            }
            loader.add(
                this.iCmsBlockResource.retrieve(
                    {
                        bareUrl: NPaypalPayInThreeMessageComponent.ID,
                        createOnError: true
                    },
                    params
                )
            );
            loader.add(
                this.iCmsBlockResource.retrieve(
                    {
                        bareUrl: NCmsProductKlarnaMsg.ID,
                        createOnError: true
                    },
                    params
                )
            );
        }
        loader.add(this.vipService.loadForProducts([this]));
    }

    protected override getRelatedProductsToLoad(params: TLoadDependeciesParams): number[] {
        const ids = super.getRelatedProductsToLoad(params);
        const parentId = (<IProductDataV2>this.data).parent;
        if (this.verbosity! > 1 && parentId && !ids.includes(parentId)) {
            ids.push(parentId);
        }
        return ids;
    }

    get familyIds() {
        return this.family.map(i => i.id);
    }
    override get defaultChild(): Product | undefined {
        if (this.hasV3) {
            const data = this.data as IProductDataV3;
            if (data.default_product_id_shown) {
                return this.existingChildren.find(item => item.id === data.default_product_id_shown);
            }
        }
        return super.defaultChild;
    }

    get productStatus(): EProductStatus {
        if (!this.hasV2 || !this.data) {
            return EProductStatus.unknown;
        }
        return this.cacheService.get(
            ['productStatus', this.id],
            () => {
                const data = this.data as IProductDataV2;
                if (data.force_coming_soon) {
                    return EProductStatus.comingSoonForced;
                }
                if (data.phase === EProductPhase.new && this.isOutOfStock) {
                    return EProductStatus.comingSoon;
                }
                if (data.phase === EProductPhase.new && this.backOrderOnly) {
                    return EProductStatus.preorder;
                }
                if (this.tradeStoreService.isTradeStore()) {
                    if (data.phase === EProductPhase.current && this.backOrderOnly) {
                        return EProductStatus.backorder;
                    }
                }
                if (data.phase === EProductPhase.current && this.isOutOfStock) {
                    return EProductStatus.backSoon;
                }

                if (data.phase === EProductPhase.discontinued && this.isOutOfStock) {
                    return EProductStatus.soldOut;
                }
                return EProductStatus.unknown;
            },
            { ttl: 1000 * 30 }
        );
    }

    /**
     * Gets final listing label value
     */
    override getListingLabel(): number | undefined {
        if (!this.hasV2) {
            return undefined;
        }

        const label = (this.data as IProductDataV2).listing_label;
        if (label) {
            return label;
        }
        switch (this.productStatus) {
            case EProductStatus.comingSoonForced:
                return EListingLabelOptionId.comingSoon;
            case EProductStatus.comingSoon:
                return EListingLabelOptionId.comingSoon;
            case EProductStatus.preorder:
                return EListingLabelOptionId.preOrder;
            case EProductStatus.backSoon:
                return EListingLabelOptionId.backSoon;
            case EProductStatus.backorder:
                return EListingLabelOptionId.backOrder;
            case EProductStatus.soldOut:
                return EListingLabelOptionId.soldOut;
        }
        if (this.isSale) {
            return EListingLabelOptionId.sale;
        }

        if (this.isNew) {
            return EListingLabelOptionId.newIn;
        }
        return undefined;
    }
    get isComingSoonForced(): boolean {
        return this.productStatus === EProductStatus.comingSoonForced;
    }
    get isComingSoon(): boolean {
        return this.productStatus === EProductStatus.comingSoon;
    }
    get isBackSoon(): boolean {
        return this.productStatus === EProductStatus.backSoon;
    }
    get isPreOrder(): boolean {
        return this.productStatus === EProductStatus.preorder;
    }
    get isBackOrder(): boolean {
        return this.productStatus === EProductStatus.backorder;
    }

    get isSoldOut(): boolean {
        return this.productStatus === EProductStatus.soldOut;
    }
    get isSale(): boolean {
        if (this.data?.org_price || (!this.tradeStoreService.isTradeStore() && this.currentGroupOrgPrice)) {
            return true;
        }
        return false;
    }

    private get currentGroupOrgPrice(): number | undefined {
        if (this.data?.group_org_price && this.app.customer.model.data.group_id) {
            return this.data!.group_org_price[this.app.customer.model.data.group_id] || undefined;
        }
        return undefined;
    }

    private get currentGroupPrice(): number | undefined {
        if (this.data?.group_price && this.app.customer.model.data.group_id) {
            return this.data.group_price[this.app.customer.model.data.group_id] || undefined;
        }
        return undefined;
    }

    get tradeSalePrice(): number | undefined {
        if (this.orgPrice && this.currentGroupPrice) {
            if (this.price < this.currentGroupPrice) {
                return this.price;
            } else {
                return this.currentGroupPrice;
            }
        } else if (this.orgPrice) {
            return this.price;
        } else {
            return undefined;
        }
    }
    /**
     * Product page layout variant
     */
    get ppLayoutVariant(): string {
        return this.getAttr<string>('product_page') || 'default';
    }
    get labelStyle() {
        switch (this.getListingLabel()) {
            case EListingLabelOptionId.preOrder:
            case EListingLabelOptionId.backOrder:
            case EListingLabelOptionId.comingSoon:
            case EListingLabelOptionId.backSoon:
            case EListingLabelOptionId.newIn:
                return {
                    'background-color': this.labelColor || 'white'
                };
            case EListingLabelOptionId.sale:
                return {
                    color: 'white',
                    'background-color': this.labelColor || 'white'
                };
            default:
                return {
                    'background-color': '#FAFAFA'
                };
        }
    }

    getProperties(): string {
        const data = this.data as IProductData | undefined;
        if (data) {
            return [
                { code: 'color', prefix: 'Colour' },
                { code: 'size', prefix: 'Size' }
            ]
                .filter(item => !!data[item.code])
                .map(item => `${item.prefix} ${optionLabelPipe(data[item.code], item.code)}`)
                .join(' | ');
        }
        return undefined;
    }
    /**
     * If product is configurable with size option
     */
    get hasSizeOption(): boolean {
        const data = this.data as IProductDataV2;
        return data && !data.isBanner && !!data.options?.configurable?.['size'];
    }
    get backOrderOnly() {
        return this.isBackorderOnly();
    }
    get klarnaPrice() {
        return parseFloat((this.price / 3).toFixed(2));
    }
    get sizeGuideVariant() {
        return (this.data as IProductDataV3)?.size_guide?.toString() || (this.parent.data as IProductDataV3)?.size_guide?.toString();
    }
    override getOrgPrice(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>, taxMode: TaxMode = TaxMode.auto): number | undefined {
        const superOrgPrice = this.calcPrice(true, request, taxMode, true);
        const defaultPrice = this.calcPrice(false, request, taxMode, true);
        const orgPrice = defaultPrice && superOrgPrice ? Math.max(defaultPrice, superOrgPrice) : defaultPrice || superOrgPrice;
        if (orgPrice && orgPrice > this.price) {
            return orgPrice;
        }
        return undefined;
    }
    get productFeaturesVariant(): string {
        return `product-features.${this.parent.id}`;
    }

    get productTechnicalVariant(): string {
        return `product-technical.${this.parent.id}`;
    }

    get productGalleryOutletVariant(): string {
        return `product-gallery.${this.parent.id}`;
    }

    get productFeaturesCarouselVariant(): string {
        return `product-feature-carousel.${this.parent.id}`;
    }

    get productAthleteVariant(): string {
        return `product-athlete.${this.parent.id}`;
    }

    get carouselVariant(): string {
        return `${this.parent.id}`;
    }

    get media(): IProductMediaItem[] {
        return (
            filterMediaByLabel(
                this.familyMedia?.filter(media => media?.labels?.length),
                HIDDEN_LABEL_MEDIA,
                true
            ) || []
        );
    }
    get minQty(): number {
        if (this.hasV2 && this.tradeStoreService.isTradeStore()) {
            const minQty = (<IProductDataV2>this.data).min_sales_qty_website || 1;
            const increment = this.increment || 1;
            if (minQty < increment) {
                return increment;
            } else if (minQty > increment) {
                const rest = minQty % increment;
                if (rest) {
                    const missing = increment - rest;
                    return minQty + missing;
                }
            }
            return minQty;
        }
        return 1;
    }
    get increment(): number {
        if (this.hasV2 && this.tradeStoreService.isTradeStore()) {
            const increment = (<IProductDataV2>this.data).qty_increment_website;
            if (increment && typeof increment === 'number') {
                return increment;
            }
        }
        return 1;
    }
    get inStockEstimationMessage(): string | undefined {
        const inStockDate = new Date((<IProductDataV2>this.data).in_stock_date);
        const currentDate = new Date();

        if ((this.isNotifyMe || this.backOrderOnly) && inStockDate > currentDate) {
            return formatDate(inStockDate, this.uiConfig.date.format, this.app.data.locale);
        }
        return undefined;
    }
    get isNotifyMe(): boolean {
        return this.isComingSoon || this.isComingSoonForced || this.isBackSoon;
    }

    get iCmsBlockOrder(): TProductCmsSection[] {
        if ((this.data as IProductDataV2).parent) {
            return (this.parent as LocalProduct).iCmsBlockOrder;
        }
        const defaultOrder: TProductCmsSection[] = [
            'technical',
            'features',
            'highlights',
            'specs',
            'video',
            'tech',
            'athlete',
            'editorial',
            'aswornby',
            'reviews',
            'carousel'
        ];
        if (!this.hasV3) {
            return defaultOrder;
        }
        if (!this.cache['_iCmsBlockOrder']) {
            const local =
                (this.data as IProductDataV3).icms_block_order
                    ?.toLowerCase()
                    .split(',')
                    .map(i => i.trim() as TProductCmsSection)
                    .filter(i => defaultOrder.includes(i)) || [];
            this.cache['_iCmsBlockOrder'] = [...local, ...defaultOrder.filter(i => !local.includes(i))];
        }
        return this.cache['_iCmsBlockOrder'];
    }

    canUseIncrementAsMinQty(): boolean {
        if (!this.customOptions?.length) {
            return !!this.app.customer.basket.byProduct(this.id);
        }
        return false;
    }
    validateQty(value: number, useIncrementAsMinQty = false): number {
        if (typeof value === 'number') {
            const minQty = useIncrementAsMinQty && this.canUseIncrementAsMinQty() ? this.increment : this.minQty;
            if (value < minQty) {
                return minQty;
            } else if (value > minQty) {
                const increment = this.increment;
                if (increment > 1) {
                    const rest = value % increment;
                    if (rest !== 0) {
                        return value - rest;
                    }
                }
            }
        }
        return value;
    }

    get attrsOptionsOrder(): NAttributeOrder.IContent[] | undefined {
        return this.iCmsConfigResource?.getModelByUrl(NAttributeOrder.ID)?.data?.content?.attrs;
    }

    get extendedFamilyIds(): number[] {
        const extendedFamilyIds = (this.family
            .map(i => {
                const ids = (i.data as IProductDataV2)?.inherit_reviews_from_following;
                return !!ids && typeof ids === 'string' ? ids?.split(',') : ids;
            })
            ?.flat()
            ?.filter(i => !!i)
            ?.map(id => (typeof id === 'number' ? id : Number(id!.trim()))) || []) as number[];
        return [...this.familyIds, ...extendedFamilyIds];
    }
}

ProductResource.instance = LocalProduct;
